import React from "react"
import {Col, Row, Tooltip} from "antd"
import styles from "./FinAidPackageInfo.module.css"
import {Auth} from "types/auth"
import {useTranslation} from "react-i18next"
import {FinAidOffer, HousingPlan} from "types/fin-aid/fin-aid"
import {finAidService} from "services"
import cx from "classnames"
import {Icon} from "components"
import {useVisible} from "hooks"
import {Settings} from "types/settings"
import EditableStudentCustomFields from "components/EditableStudentCustomFields"

type Props = {
    student?: Auth.DepartmentStudent
    isPanel?: boolean
}

export function FinAidPackageInfo({student, isPanel = false}: Props) {
    const {t} = useTranslation(["financialAid"])
    const detailVisible = useVisible(true)
    const [finAidOffer, setFinAidOffer] = React.useState<FinAidOffer | undefined>()

    React.useEffect(() => {
        ;(async function loadLatestFinAidOffer() {
            if (!student?.profileId) return
            setFinAidOffer(
                await finAidService.getFinAidLatestOffer({
                    studentProfileId: student.profileId,
                    linkedPackage: true
                })
            )
        })()
    }, [student?.profileId])

    const awardYear = finAidOffer?.awardYear ? `${finAidOffer.awardYear} - ${finAidOffer.awardYear + 1}` : "-"

    const dependencyStatus =
        student?.dependencyStatus || finAidOffer?.finAidPackage?.dependencyStatus
            ? t(
                  `financialAidPackage.dependencyStatuses.${
                      student?.dependencyStatus || finAidOffer?.finAidPackage?.dependencyStatus
                  }`
              )
            : "-"

    const payingMethods = student?.payingMethods?.map((method) => method.name).join(", ") || "-"

    const finAidStatus = finAidOffer?.status ? t(`finAidOffer.status.${finAidOffer?.status}`) : "-"

    const housingPlan = student.housingPlan ? t(`financialAid:housingPlan.${student.housingPlan}`) : "-"

    const isir = finAidOffer?.finAidPackage?.isir || "-"

    if (isPanel) {
        return (
            <div className={styles.panelWrap}>
                <div className={styles.headingWrap}>
                    <div className={styles.headingTitle}>Financial Aid Details</div>
                    <button
                        className={cx(styles.collapseButton, {[styles.collapsed]: detailVisible.isVisible})}
                        onClick={detailVisible.toggle}>
                        <Icon icon="ARROW_DOWN" color="#666666" className={styles.headingIcon} />
                    </button>
                </div>

                {detailVisible.isVisible && (
                    <>
                        <div className={styles.contentWrap}>
                            <div className={styles.contentTitle}>Dependency Status</div>
                            <Tooltip title={dependencyStatus}>
                                <div className={styles.contentValue} title={dependencyStatus}>
                                    {dependencyStatus}
                                </div>
                            </Tooltip>
                        </div>
                        <div className={styles.contentWrap}>
                            <div className={styles.contentTitle}>Number of Dependents</div>
                            <div className={styles.contentValue}>{student.numberOfDependents}</div>
                        </div>
                        <div className={styles.contentWrap}>
                            <div className={styles.contentTitle}>Method of paying for College</div>
                            <Tooltip title={payingMethods}>
                                <div className={styles.contentValue} title={payingMethods}>
                                    {payingMethods}
                                </div>
                            </Tooltip>
                        </div>
                        <div className={styles.contentWrap}>
                            <div className={styles.contentTitle}>Housing Plan</div>
                            <Tooltip title={housingPlan}>
                                <div className={styles.contentValue} title={housingPlan}>
                                    {housingPlan}
                                </div>
                            </Tooltip>
                        </div>
                        <div className={styles.contentWrap}>
                            <div className={styles.contentTitle}>Current Award Year</div>
                            <div className={styles.contentValue} title={awardYear}>
                                {awardYear}
                            </div>
                        </div>
                        <div className={styles.contentWrap}>
                            <div className={styles.contentTitle}>Financial Aid Package Status</div>
                            <Tooltip title={finAidStatus}>
                                <div className={styles.contentValue} title={finAidStatus}>
                                    {finAidStatus}
                                </div>
                            </Tooltip>
                        </div>
                        <div className={styles.contentWrap}>
                            <div className={styles.contentTitle}>Current ISIR</div>
                            <Tooltip title={isir}>
                                <div className={styles.contentValue} title={isir}>
                                    {isir}
                                </div>
                            </Tooltip>
                        </div>
                        <EditableStudentCustomFields
                            student={student}
                            module={Settings.CustomFieldModule.FinancialAid}
                        />
                    </>
                )}
            </div>
        )
    }

    return (
        <div className={styles.finAidInfo}>
            <Row gutter={[8, 8]}>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Current Award Year</div>
                        <div className={styles.infoValue} title={awardYear}>
                            {awardYear}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Dependency Status</div>
                        <div className={styles.infoValue} title={dependencyStatus}>
                            {dependencyStatus}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Method of paying for College</div>
                        <div className={styles.infoValue} title={payingMethods}>
                            {payingMethods}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Financial Aid Package Status</div>
                        <div className={styles.infoValue} title={finAidStatus}>
                            {finAidStatus}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    )
}
